import {
  type CounterpartOnlineOfferResponseBody,
  type MoneyResponseBody,
  type SaleVehiclePhotoResponseBody,
  type UserInfoResponseBody,
  type VehicleV2ResponseBody,
  type EngagementReport,
} from '@online-offer/shared';
import {z} from 'zod';

import {
  createFinancialProductPreferenceConflictResponseBodySchema,
  createFinancialProductPreferenceRefusalSchema,
  type FinancialProductPreferenceCoordinates,
  FinancialProductsResponseBodyBaseSchema,
} from 'features/financial-products';

export {
  FinancialProductCalculationSchema,
  FinancialProductOfferSchema,
  FinancialProductPreferenceCoordinatesSchema,
  FinancialProductPreferenceRefusalReasonSchema,
  FinancialProductSelectionSchema,
  FinancialProductTypeSchema,
  FinancialProductUnavailableReasonSchema,
  LeasingCellSchema,
  LeasingCostsSchema,
  LeasingMonthlyServiceKindSchema,
  LeasingOneTimeItemKindSchema,
  LeasingTargetGroupSchema,
} from 'features/financial-products';
export type {
  FinancialProductCalculation,
  FinancialProductOffer,
  FinancialProductPreferenceCoordinates,
  FinancialProductPreferenceRefusalReason,
  FinancialProductSelection,
  FinancialProductType,
  FinancialProductUnavailableReason,
  LeasingCell,
  LeasingCosts,
  LeasingMonthlyServiceKind,
  LeasingOneTimeItemKind,
  LeasingTargetGroup,
} from 'features/financial-products';

export type SendOnlineOfferEngagementApiArg = {
  offerId: string;
  workspace: string;
} & EngagementReport & {sessionId: string};

export type GetBusinessCaseOnlineOfferApiResponse =
  /** status 200  */ BusinessCaseOnlineOfferResponseBody;
export type GetBusinessCaseOnlineOfferApiArg = {
  offerId: string;
  /** Identify tenant with his workspace name */
  workspace: string;
  openedBy?: string | null;
  /** Anonymous browser-session id; only for customer-facing links (openedBy=email) */
  sessionId?: string | null;
};
export type BigDecimal = string;
export type Currency = string;
export type Money = {
  amount: BigDecimal;
  currency: Currency;
};
export type PriceWithVatDirection = 'FROM_PRICE_WITHOUT_VAT' | 'FROM_PRICE_WITH_VAT';
export type UpsellPriceResponseBody = {
  withoutVat: Money | null;
  withVat: Money | null;
  vatRate: string | null;
  vatCode: string | null;
  percentage: string | null;
  direction: PriceWithVatDirection | null;
};
export type DiscountResponseBody = {
  withoutVat: Money | null;
  withVat: Money | null;
  vatRate: BigDecimal | null;
  vatCode: string | null;
  percentage: BigDecimal | null;
};
export type UpsellResponseBody = {
  id: string;
  name: string | null;
  purchasePrice: UpsellPriceResponseBody | null;
  catalogPrice: UpsellPriceResponseBody | null;
  sellingPrice: UpsellPriceResponseBody | null;
  discountPrice: DiscountResponseBody | null;
  margin: string | null;
  profitPrice: UpsellPriceResponseBody | null;
};
export const AdditionalFieldsKeysSchema = z.enum(['affiliatedEntity']);

export type AdditionalFieldsKeys = z.infer<typeof AdditionalFieldsKeysSchema>;
export const CustomFieldValueSchema = z.object({
  definitionId: AdditionalFieldsKeysSchema,
  value: z.object({
    type: z.enum(['integer', 'decimal', 'string', 'money', 'date', 'datetime', 'boolean']),
    value: z.any(),
  }),
});
export type CustomFieldValue = z.infer<typeof CustomFieldValueSchema>;
export type VehicleReservationCustomerResponseBody = {
  id: string;
  name: string;
};
export type VehicleReservationBusinessCaseResponseBody = {
  id: string;
  code: string;
};
export type VehicleReservationDepositResponseBody = {
  incomeCashReceiptId: string;
  expenseCashReceiptId: string | null;
};
export type VehicleReservationUserResponseBody = {
  id: string;
  fullName: string;
};
export type VehicleReservationResponseBody = {
  reservedUntil: string;
  reservedUntilAt: string;
  isExpired: boolean;
  note: string | null;
  customer: VehicleReservationCustomerResponseBody | null;
  businessCase: VehicleReservationBusinessCaseResponseBody | null;
  paidDeposit: VehicleReservationDepositResponseBody | null;
  refundedDeposits: VehicleReservationDepositResponseBody[];
  createdAt: string;
  createdBy: VehicleReservationUserResponseBody;
  canceledAt: string | null;
  canceledBy: VehicleReservationUserResponseBody | null;
};
export type CustomerDiscriminator = 'NATURAL_PERSON' | 'BUSINESS';
export type CustomerSource = 'WALK_IN' | 'INBOUND_CALL' | 'OUTBOUND_CALL';
export type PhoneNumberResponseBody = {
  type: string | null;
  countryCode: string;
  prefix: string;
  number: string;
};
export type EmailResponseBody = {
  type: string | null;
  email: string;
};
export type IdentityCardResponseBodyV2 = {
  id: string;
  type: string | null;
  cardNumber: string;
  issuedOn: string | null;
  validUntil: string | null;
  issuer: string | null;
  issuedInCountryCode: string | null;
  note: string | null;
};
export type CoordinatesResponseBody = {
  latitude: string;
  longitude: string;
};
export type CommonAddressResponseBody = {
  street: string;
  descriptiveNumber: string | null;
  orientationNumber: string | null;
  city: string;
  zip: string;
  addressComplement: string | null;
  country: string;
  prefix: string | null;
  district: string | null;
  state: string | null;
  postal: string | null;
  coordinates: CoordinatesResponseBody | null;
  externalAddressId: string | null;
};
export type OnlineOfferTestDriveResponseBody = {
  id: string;
  scheduledAt: string;
  returnedAt: string | null;
  state: 'planned' | 'completed';
  note: string | null;
  customerName: string | null;
  branchAddress: CommonAddressResponseBody | null;
};
export type AddressResponseBodyV2 = {
  id: string;
  address: CommonAddressResponseBody;
  type: string | null;
  invalid: boolean | null;
};
export type PersonResponseBodyV2 = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  titleBefore: string | null;
  titleAfter: string | null;
  genderKey: string | null;
  roles: string | null;
  citizenshipCode: string | null;
  birthdate: string | null;
  personalIdentifier: string | null;
  phoneNumbers: PhoneNumberResponseBody[];
  emails: EmailResponseBody[];
  identityCards: IdentityCardResponseBodyV2[];
  permanentAddress: AddressResponseBodyV2 | null;
};
export type BusinessInfoDataResponseBody = {
  countryOfRegistrationCode: string | null;
  registrationNumber: string | null;
  vatNumber: string | null;
  tradeName: string | null;
  fileNumber: string | null;
};
export type BusinessInfoResponseBodyV2 = {
  id: string;
  businessInfo: BusinessInfoDataResponseBody;
  address: AddressResponseBodyV2 | null;
};
export type LegalForm = 'LEGAL_ENTITY' | 'NATURAL_PERSON' | 'SELF_EMPLOYED';
export type BankAccountResponseBodyV2 = {
  id: string;
  name: string | null;
  countryCode: string | null;
  ownerName: string | null;
  iban: string | null;
  swiftBic: string | null;
  currency: string | null;
  number: string | null;
  bankCode: string | null;
};
export type ContractInformationResponseBodyV2 = {
  id: string;
  permanent: boolean;
  legalForm: LegalForm;
  bankAccounts: BankAccountResponseBodyV2[];
  person: PersonResponseBodyV2 | null;
  businessInfo: BusinessInfoResponseBodyV2 | null;
  customFieldsPayload: CustomFieldValue[];
  isDeleted: boolean;
};
export type Institution = 'INSURANCE' | 'SUPPLIER' | 'FLEET' | 'LOAN' | 'LEASING' | 'IMPORTER';
export type CustomerResponseBodyV2 = {
  id: string;
  discriminator: CustomerDiscriminator;
  source: CustomerSource | null;
  foundingPerson: PersonResponseBodyV2 | null;
  businessInfo: BusinessInfoResponseBodyV2 | null;
  contacts: PersonResponseBodyV2[];
  persons: PersonResponseBodyV2[];
  contractInformation: ContractInformationResponseBodyV2[];
  addresses: AddressResponseBodyV2[];
  selfEmployed: boolean | null;
  selfEmployedBusinessInfoData: BusinessInfoDataResponseBody | null;
  selfEmployedBusinessAddress: AddressResponseBodyV2 | null;
  publicId: string | null;
  institutions: Institution[] | null;
  customerGroups: string[];
  abroadSale: string | null;
};
export type WarrantyTermsAndConditionsResponseBody = {
  locale: string;
  text: string;
};
export type OnlineOfferAfterSaleRatingResponseBody = {
  rating: number;
  recommendation: number | null;
  comment: string | null;
  submittedAt: string;
};
export type OnlineOfferAfterSaleResponseBody = {
  closedAt: string;
  rating: OnlineOfferAfterSaleRatingResponseBody | null;
};
export type SubmitOnlineOfferRatingApiArg = {
  offerId: string;
  workspace: string;
  rating: number;
  recommendation: number | null;
  comment: string | null;
};
export type RefuseOnlineOfferCommunicationApiArg = {
  offerId: string;
  workspace: string;
};
export type IllustrativePhotoResponseBody = {
  fileId: string;
  /** Signed storage URL of the photo. */
  url: string;
};

export type BusinessCaseOnlineOfferResponseBody = {
  afterSale: OnlineOfferAfterSaleResponseBody | null;
  businessCasePublicId: string | null;
  businessCaseCustomId: string | null;
  contactPersonId: string | null;
  carAuditId: string | null;
  carAuditThemeId: string | null;
  totalPriceWithVat: MoneyResponseBody | null;
  totalPriceWithoutVat: MoneyResponseBody | null;
  totalDiscountPriceWithVat: MoneyResponseBody | null;
  totalDiscountPriceWithoutVat: MoneyResponseBody | null;
  vehiclePriceWithVat: MoneyResponseBody | null;
  vehiclePriceWithoutVat: MoneyResponseBody | null;
  originalCarPriceWithVat: MoneyResponseBody | null;
  originalCarPriceWithoutVat: MoneyResponseBody | null;
  advertisementDescription: string;
  upsell: UpsellResponseBody[];
  saleVehiclePhotos: SaleVehiclePhotoResponseBody[];
  /** The live catalogue photo of the deal vehicle's configuration, sent only when the car has no photos of its own. */
  illustrativePhoto: IllustrativePhotoResponseBody | null;
  owner: UserInfoResponseBody | null;
  /** Null only for a Business Case with offer alternatives but no deal vehicle yet. */
  vehicle: VehicleV2ResponseBody | null;
  activeReservation: VehicleReservationResponseBody | null;
  testDrive: OnlineOfferTestDriveResponseBody | null;
  customer: string | null;
  activeChatConversationId: string | null;
  disclaimer: string;
  vehicleCertificateFileUri: string | null;
  carPassDocumentPublicUrl: string | null;
  carPassDocumentFileId: string | null;
  carPassDocumentDownloadedAt: string | null;
  offerValidTo: string | null;
  /** Warranty duration in months */
  warrantyDuration: number | null;
  warrantyProgramName: string | null;
  /** Warranty terms and conditions (plain text), one entry per content locale */
  warrantyTermsAndConditions: WarrantyTermsAndConditionsResponseBody[];
  cebiaSmartCodeUrl: string | null;
  counterpart: CounterpartOnlineOfferResponseBody | null;
};
export type Error = {
  error?: {
    message?: string;
    data?: {
      [key: string]: string;
    };
  };
  errors?: {
    message?: string;
    code?: string;
  }[];
  validationErrors?: {
    message?: string;
    field?: string;
  }[];
};

export type ListLogosApiResponse = /** status 200  */ ListLogosResponseBody;
export type ListLogosApiArg = {
  /** Identify tenant with his workspace name */
  'X-Workspace': string;
};

export type LogoResponseBody = {
  uri: string | null;
};
export type ListLogosResponseBody = {
  logos: {
    [key: string]: LogoResponseBody;
  };
};

export type GetPdfByOfferIdApiArg = {
  offerId: string;
  workspace: string;
  locale: string;
  filename: string;
};

export type GetPdfByOfferIdApiResponse = {
  /** URL to the generated PDF */
  uri: string;
};

/**
 * Public payloads must not expose seller identity. Tests use the strict envelope; runtime parsing
 * strips unknown members before caching, while the backend remains the privacy boundary.
 */
export const PublicFinancialProductsResponseBodySchema =
  FinancialProductsResponseBodyBaseSchema.strict();
export type PublicFinancialProductsResponseBody = z.infer<
  typeof PublicFinancialProductsResponseBodySchema
>;

export type GetOnlineOfferFinancialProductsApiResponse =
  /** status 200 */ PublicFinancialProductsResponseBody;
export type GetOnlineOfferFinancialProductsApiArg = {
  offerId: string;
  /** Identify tenant with his workspace name */
  workspace: string;
  /** Sent as `Accept-Language`; decides the provider display name. */
  language: string;
};

export type UpdateFinancialProductPreferenceApiArg = {
  offerId: string;
  /** Identify tenant with his workspace name */
  workspace: string;
  language: string;
  coordinates: FinancialProductPreferenceCoordinates;
  /** The last-read revision; a stale one is refused as a conflict. */
  expectedPreferenceRevision: number;
};
export type UpdateFinancialProductPreferenceApiResponse =
  /** status 200 */ PublicFinancialProductsResponseBody;

/** A refused write carries the authoritative state that superseded it. */
export const FinancialProductPreferenceConflictResponseBodySchema =
  createFinancialProductPreferenceConflictResponseBodySchema(
    PublicFinancialProductsResponseBodySchema
  );

/**
 * Only recoverable write failures are modelled. Temporary HTTP failures, including an edge block
 * (403 from the WAF), are retried by isRetryableWriteError; unreachable requests wait for
 * connectivity, while all other failures remain outside the refusal flow.
 */
export const FinancialProductPreferenceRefusalSchema = z.union([
  createFinancialProductPreferenceRefusalSchema(
    FinancialProductPreferenceConflictResponseBodySchema
  ).transform((refusal) => ({kind: 'REFUSED' as const, ...refusal})),
  z.object({status: z.literal('FETCH_ERROR')}).transform(() => ({kind: 'UNREACHABLE' as const})),
]);
export type FinancialProductPreferenceRefusal = z.infer<
  typeof FinancialProductPreferenceRefusalSchema
>;
